import { useEffect, useState } from "react";
import withLoader from "../../components/layouts/WithLoader";
import SocieteService from "../../services/Societe";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import KPIAchatService from "../../services/KPIAchatService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  MONTHS_FR,
  fmt3,
  byIdMonth,
  computePrevision,
  buildChart,
  mergedSum,
  chartOpts,
  EvoBadge,
} from "../../utils/kpiChartHelpers";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const countOf = (data, id) => {
  const item = Array.isArray(data) ? data.find((f) => f?.id === id) : null;
  return item?.count || 0;
};

const KPIAchat = ({ startLoading, stopLoading }) => {
  const currentYear = new Date().getFullYear();
  const [finalYear, setFinalYear] = useState(currentYear);
  const [yearsOptions, setYearsOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState({ value: currentYear, label: currentYear });
  const animatedComponents = makeAnimated();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // année N
  const [toByYear, setToByYear] = useState([]);
  const [facturesPayed, setFacturesPayed] = useState([]);
  // année N-1
  const [toByYear_p, setToByYear_p] = useState([]);
  const [facturesPayed_p, setFacturesPayed_p] = useState([]);

  const handleYearChange = (selectedOption) => {
    setSelectedYear(selectedOption);
    setFinalYear(selectedOption.label);
  };

  const getKPIAchat = (year = finalYear) => {
    const uid = getCurrentUserId();
    const yp = Number(year) - 1;
    setError(false);
    startLoading();
    Promise.all([
      KPIAchatService.getToByYear(uid, year),
      KPIAchatService.getToByYear(uid, yp),
      KPIAchatService.numberFacturePayed(uid, year),
      KPIAchatService.numberFacturePayed(uid, yp),
    ])
      .then(([r1, p1, r2, p2]) => {
        setToByYear(r1?.data?.success || r1?.data || []);
        setToByYear_p(p1?.data?.success || p1?.data || []);
        setFacturesPayed(r2?.data?.success || r2?.data || []);
        setFacturesPayed_p(p2?.data?.success || p2?.data || []);
        setLoaded(true);
      })
      .catch((e) => {
        console.log("error: " + e);
        setError(true);
      })
      .finally(() => {
        stopLoading();
      });
  };

  useEffect(() => {
    SocieteService.getFinacialYearsAndMonthsFromSocieteCreationDate()
      .then((response) => {
        if (response.data["success"] !== undefined) {
          setYearsOptions(
            response.data["success"].years.map((year, index) => ({
              value: index,
              label: year,
            }))
          );
        }
      })
      .catch((error) => {
        console.log("error: " + error);
      });
    // chargement automatique de l'année en cours
    getKPIAchat(currentYear);
  }, []);

  // ── calculs année N ──
  const initialArr = byIdMonth(toByYear, "totalAmountInitialPrice");
  const htArr = byIdMonth(toByYear, "totalAmountSellingPrice");
  const ttArr = byIdMonth(toByYear, "totalAmountPriceTT");
  const promoArr = byIdMonth(toByYear, "promotionPercentage");
  // ── année N-1 ──
  const initialArr_p = byIdMonth(toByYear_p, "totalAmountInitialPrice");
  const htArr_p = byIdMonth(toByYear_p, "totalAmountSellingPrice");
  const ttArr_p = byIdMonth(toByYear_p, "totalAmountPriceTT");
  const promoArr_p = byIdMonth(toByYear_p, "promotionPercentage");

  const totalHT = mergedSum(htArr, computePrevision(htArr));
  const totalTT = mergedSum(ttArr, computePrevision(ttArr));
  const totalInitial = mergedSum(initialArr, computePrevision(initialArr));
  const totalHT_p = htArr_p.reduce((a, b) => a + b, 0);
  const totalTT_p = ttArr_p.reduce((a, b) => a + b, 0);
  const totalInitial_p = initialArr_p.reduce((a, b) => a + b, 0);
  const totalTVA = totalTT - totalHT;
  const totalTVA_p = totalTT_p - totalHT_p;

  const payed = countOf(facturesPayed, 1);
  const notPayed = countOf(facturesPayed, 0);
  const payed_p = countOf(facturesPayed_p, 1);
  const notPayed_p = countOf(facturesPayed_p, 0);

  const prevYear = Number(finalYear) - 1;

  const barHT = buildChart(htArr, htArr_p, finalYear, prevYear, "rgba(30,136,229,0.85)", "rgba(30,136,229,0.25)", "rgba(30,136,229,0.3)");
  const barTT = buildChart(ttArr, ttArr_p, finalYear, prevYear, "rgba(0,188,212,0.85)", "rgba(0,188,212,0.25)", "rgba(0,188,212,0.3)");
  const barInitial = buildChart(initialArr, initialArr_p, finalYear, prevYear, "rgba(255,152,0,0.85)", "rgba(255,152,0,0.25)", "rgba(255,152,0,0.3)");
  // pas de prévision pour la promotion : un mois sans remise reste à 0 %
  const barPromo = {
    labels: MONTHS_FR,
    datasets: [
      {
        label: `${finalYear}`,
        data: promoArr,
        backgroundColor: "rgba(156,39,176,0.85)",
        borderWidth: 0,
      },
      {
        label: `${prevYear}`,
        data: promoArr_p,
        backgroundColor: "rgba(156,39,176,0.3)",
        borderWidth: 0,
      },
    ],
  };
  const promoChartOpts = {
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true } },
  };

  const doughnutTva = {
    labels: ["Achats HT", "TVA"],
    datasets: [
      {
        data: [fmt3(totalHT), fmt3(totalTVA)],
        backgroundColor: ["#1e88e5", "#e53935"],
      },
    ],
  };
  const doughnutPayed = {
    labels: ["Factures payées", "Factures non payées"],
    datasets: [
      {
        data: [payed, notPayed],
        backgroundColor: ["#28a745", "#dc3545"],
      },
    ],
  };

  const kpiCards = [
    { icon: "fas fa-file-invoice-dollar", label: "Achats HT", curr: totalHT, prev: totalHT_p },
    { icon: "fas fa-dollar-sign", label: "Achats TTC", curr: totalTT, prev: totalTT_p },
    { icon: "fas fa-hand-holding-usd", label: "Total TVA", curr: totalTVA, prev: totalTVA_p },
    { icon: "fas fa-tags", label: "Prix initial", curr: totalInitial, prev: totalInitial_p },
    { icon: "fas fa-check-circle", label: "Factures payées", curr: payed, prev: payed_p },
    { icon: "fas fa-exclamation-circle", label: "Factures non payées", curr: notPayed, prev: notPayed_p, goodWhenUp: false },
  ];

  const chartCard = (title, icon, content, cols = "col-md-6") => (
    <div className={cols}>
      <div className="card card-outline card-dark" style={cardStyle}>
        <div className="card-header">
          <h3 className="card-title">
            <i className={`${icon} mr-2`}></i>
            {title}
          </h3>
        </div>
        <div className="card-body">{content}</div>
      </div>
    </div>
  );

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-chart-bar mr-2"></i>Dashboard achats
                <span className="badge badge-dark ml-2">{finalYear}</span>
              </h4>
              <small className="text-muted">
                Évolution des achats par mois, comparée à l'année précédente, avec
                prévision des mois restants
              </small>
            </div>
            <div className="d-flex align-items-end mt-2 mt-sm-0">
              <div style={{ minWidth: 160 }} className="mr-2">
                <label style={labelStyle} className="mb-1">
                  Année
                </label>
                <Select
                  value={selectedYear}
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={yearsOptions}
                  onChange={handleYearChange}
                  placeholder={String(finalYear)}
                />
              </div>
              <button className="btn btn-dark" onClick={() => getKPIAchat(finalYear)}>
                <i className="fas fa-sync-alt mr-1"></i>
                Valider
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {error && (
            <div className="alert alert-danger mt-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Impossible de charger les KPI d'achat. Vérifiez la connexion puis cliquez
              sur « Valider ».
            </div>
          )}

          {loaded && (
            <>
              <div className="row mt-3">
                {kpiCards.map((k) => (
                  <div className="col-xl-2 col-md-4 col-6" key={k.label}>
                    <div
                      className="d-flex align-items-center p-3 mb-3 bg-white"
                      style={{ ...cardStyle, borderLeft: "4px solid #343a40" }}
                    >
                      <span
                        className="d-flex align-items-center justify-content-center bg-dark text-white mr-3"
                        style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0 }}
                      >
                        <i className={k.icon}></i>
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <small
                          className="text-muted text-uppercase d-block text-truncate"
                          style={{ fontWeight: 600, letterSpacing: "0.03em", fontSize: "0.7rem" }}
                          title={k.label}
                        >
                          {k.label}
                        </small>
                        <span style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                          {fmt3(k.curr)}
                        </span>
                        <div className="d-flex align-items-center" style={{ gap: 6 }}>
                          <small className="text-muted">
                            {prevYear} : {fmt3(k.prev)}
                          </small>
                          <EvoBadge curr={k.curr} prev={k.prev} goodWhenUp={k.goodWhenUp !== false} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row">
                {chartCard("Achats H.T", "fas fa-chart-bar", <Bar data={barHT} options={chartOpts("")} />)}
                {chartCard("Achats T.T", "fas fa-chart-bar", <Bar data={barTT} options={chartOpts("")} />)}
              </div>

              <div className="row">
                {chartCard(
                  `Répartition HT / TVA (${finalYear}, avec prévisions)`,
                  "fas fa-chart-pie",
                  <Doughnut
                    data={doughnutTva}
                    options={{ responsive: true, plugins: { legend: { position: "top" } } }}
                  />,
                  "col-md-6 col-lg-3"
                )}
                {chartCard(
                  `Factures payées / non payées (${finalYear})`,
                  "fas fa-chart-pie",
                  <Doughnut
                    data={doughnutPayed}
                    options={{ responsive: true, plugins: { legend: { position: "top" } } }}
                  />,
                  "col-md-6 col-lg-3"
                )}
                {chartCard("Prix initial par mois", "fas fa-chart-bar", <Bar data={barInitial} options={chartOpts("")} />, "col-lg-6")}
              </div>

              <div className="row">
                {chartCard("Promotion moyenne par mois (%)", "fas fa-percent", <Bar data={barPromo} options={promoChartOpts} />)}
                <div className="col-md-6">
                  <div className="card card-outline card-dark" style={cardStyle}>
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-table mr-2"></i>
                        Résultat de l'année : {finalYear}
                      </h3>
                    </div>
                    <div className="card-body p-0 table-responsive">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Mois</th>
                            <th className="text-right">Prix initial</th>
                            <th className="text-center">Promo %</th>
                            <th className="text-right">Total HT</th>
                            <th className="text-right">Total TTC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MONTHS_FR.every((m, i) => !(htArr[i] || ttArr[i] || initialArr[i])) && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-4">
                                <i className="fas fa-chart-line fa-2x mb-2 d-block"></i>
                                Aucune donnée pour cette année
                              </td>
                            </tr>
                          )}
                          {MONTHS_FR.map((m, i) =>
                            htArr[i] || ttArr[i] || initialArr[i] ? (
                              <tr key={m}>
                                <td>
                                  <strong>{m}</strong> {finalYear}
                                </td>
                                <td className="text-right">{initialArr[i]}</td>
                                <td className="text-center">
                                  <span className="badge badge-warning">
                                    {fmt3(promoArr[i])}%
                                  </span>
                                </td>
                                <td className="text-right">{htArr[i]}</td>
                                <td className="text-right">{ttArr[i]}</td>
                              </tr>
                            ) : null
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};
export default withLoader(KPIAchat);
